import type { CSSProperties, Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import { CardLink } from "simplydo/react/ui/card";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3 } from "simplydo/react/ui/header";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import {
  IdeaUpdateStatus,
  StatusLabel,
} from "components/lib/Ideas/IdeaView/IdeaViewStyles";

import type { ChallengeIdeaListItem } from "../../types";

const IdeaCardComponent = styled(CardLink)<{ $coverImage?: string }>`
  height: 55px;
  padding-right: 10px;
  gap: 10px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  ${Button} {
    pointer-events: none;
  }
  .cover-image {
    border-radius: 5px;
    background-image: ${({ $coverImage }) =>
      $coverImage ? `url(${$coverImage})` : "none"};
    background-size: cover;
    background-position: center center;
    height: 55px;
    width: 55px;
  }
  .idea-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
  }
  .idea-name {
    display: block;
    font-weight: bold;
    color: black;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

type ConciseIdeaCardProps = { idea: ChallengeIdeaListItem; isYours: boolean };

const ConciseIdeaCard = ({ idea, isYours }: ConciseIdeaCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <IdeaCardComponent
      elevation="small"
      to={`/ideas/${idea._id}`}
      $coverImage={util.mixinCssUrlFallback(
        util.ideaCoverImage(idea, "200x200"),
        util.ideaCoverImage(undefined, "200x200"),
      )}
    >
      <div className="cover-image" />
      <div className="idea-info">
        <span className="idea-name">{idea.name}</span>
        <IdeaUpdateStatus>
          {idea.isSubmitted ? (
            <>
              <StatusLabel color="green" />
              <span>
                {t("generic.submitted")}
                {` `}
                {formatDate(idea.submittedAt, "dd/MM/yy")}
              </span>
            </>
          ) : (
            <>
              <StatusLabel color="yellow" />
              <span>
                {t("generic.updated")}
                {` `}
                {formatDate(idea.updatedAt, "dd/MM/yy")}
              </span>
            </>
          )}
        </IdeaUpdateStatus>
      </div>
      <Button
        variant={idea.isSubmitted || !isYours ? "outline" : "regular"}
        tabIndex={-1}
        aria-hidden
      >
        {idea.isSubmitted || !isYours
          ? t("generic.view")
          : t(theme.sizes.isMobile ? "generic.continue" : "ideas.ongoing.edit")}
      </Button>
    </IdeaCardComponent>
  );
};

type ChallengeMyIdeasProps = {
  challengeId: string;
  style?: CSSProperties;
  showMyIdeas: boolean;
  setShowMyIdeas: Dispatch<SetStateAction<boolean>>;
};

const ChallengeMyIdeas = ({
  challengeId,
  style,
  showMyIdeas,
  setShowMyIdeas,
}: ChallengeMyIdeasProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const myIdeasQuery = useQuery(
    initUseQuery(api.challenges.getIdeas, [challengeId, { scope: "me" }], {
      isEnabled: !!user?._id && !!challengeId,
    }),
  );
  const myIdeas = myIdeasQuery.data?.ideas || [];
  if (!myIdeas.length) return null;
  return (
    <Accordion style={style}>
      <AccordionTitle
        active={showMyIdeas}
        index={0}
        onClick={() => setShowMyIdeas((prev) => !prev)}
        style={{ paddingBottom: 0 }}
      >
        <H3 style={{ display: "inline" }}>
          {t("users.preferences.yourIdeas.yours")}
        </H3>
      </AccordionTitle>
      <AccordionContent active={showMyIdeas}>
        <Grid columns={2} mobile={1} gap="0.5rem">
          {myIdeas.map((idea) => (
            <GridColumn key={idea._id}>
              <ConciseIdeaCard
                isYours={util.canEditIdea(user, idea)}
                idea={idea}
              />
            </GridColumn>
          ))}
        </Grid>
      </AccordionContent>
    </Accordion>
  );
};

export default ChallengeMyIdeas;
